import React, { useEffect, useMemo, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Button, Divider, Spinner } from '@nextui-org/react';
import {
  Trophy,
  MapPin,
  Calendar,
  Users,
  Tv,
  Coins,
  Globe2,
  Crown,
  History,
  ChevronRight,
  Flag,
  Target,
  Award,
  BookOpen,
  ExternalLink,
  Swords,
  ListChecks,
  Star,
} from 'lucide-react';
import api from '../services/api';
import proTournamentInfoService, {
  ProTournamentInfo,
  ProTeam,
  ProKeyFact,
  ProFormatRule,
  ProPrizeBreakdown,
  ProPastWinner,
  ProScheduleEntry,
  ProRecord,
  ProNotableNineDarter,
  ProQualificationCriterion,
  ProParallelCompetition,
  ProSponsorHistoryEntry,
  ProDrawPairing,
} from '../services/proTournamentInfoService';

const ACCENT = '#3fced1';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const buildFileUrl = (path?: string | null): string | null => {
  if (!path) return null;
  if (path.startsWith('http')) return path;
  return `${api.defaults.baseURL}${path.startsWith('/') ? '' : '/'}${path}`;
};

const formatDate = (ms?: number | null): string => {
  if (!ms) return '';
  try {
    return new Date(ms).toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  } catch {
    return '';
  }
};

const formatDateRange = (t: ProTournamentInfo): string => {
  if (t.dateLabel) return t.dateLabel;
  const s = formatDate(t.startDate);
  const e = formatDate(t.endDate);
  if (s && e) return `${s} – ${e}`;
  return s || e || '';
};

// ---------------------------------------------------------------------------
// Bausteine
// ---------------------------------------------------------------------------

/** Beschreibungstext: auf Mobile eingeklappt mit "Mehr anzeigen", ab lg immer voll. */
const CollapsibleText: React.FC<{ text: string }> = ({ text }) => {
  const [expanded, setExpanded] = useState(false);
  return (
    <div>
      <div
        className={`space-y-3 whitespace-pre-line break-words text-sm leading-relaxed text-gray-300 sm:text-[15px] ${
          expanded ? '' : 'line-clamp-6 lg:line-clamp-none'
        }`}
      >
        {text}
      </div>
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="mt-3 text-sm font-semibold lg:hidden"
        style={{ color: ACCENT }}
      >
        {expanded ? 'Weniger anzeigen' : 'Mehr anzeigen'}
      </button>
    </div>
  );
};

/** Einheitliche Inhalts-Sektion (linke Spalte). */
const ContentSection: React.FC<{
  id: string;
  icon: React.ReactNode;
  eyebrow: string;
  title: string;
  children: React.ReactNode;
}> = ({ id, icon, eyebrow, title, children }) => (
  <section
    id={id}
    className="scroll-mt-32 rounded-2xl border border-zinc-800 bg-zinc-900/60 p-4 sm:p-6"
  >
    <div className="mb-4 flex items-center gap-3">
      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#3fced1]/10 text-[#3fced1]">
        {icon}
      </div>
      <div className="min-w-0">
        <p className="text-[10px] font-semibold uppercase tracking-widest text-[#3fced1]">
          {eyebrow}
        </p>
        <h2 className="text-lg font-extrabold leading-tight text-white sm:text-xl">
          {title}
        </h2>
      </div>
    </div>
    {children}
  </section>
);

/** Kompakte Karte für die Sidebar (rechte Spalte). */
const SideCard: React.FC<{
  icon: React.ReactNode;
  title: string;
  children: React.ReactNode;
}> = ({ icon, title, children }) => (
  <div className="rounded-2xl border border-zinc-800 bg-zinc-900/60 p-4">
    <div className="mb-3 flex items-center gap-2">
      <span className="text-[#3fced1]">{icon}</span>
      <h3 className="text-sm font-bold uppercase tracking-wide text-white">{title}</h3>
    </div>
    {children}
  </div>
);

const InfoRow: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <div className="flex items-start justify-between gap-3 py-2">
    <span className="shrink-0 text-xs text-gray-500">{label}</span>
    <span className="min-w-0 text-right text-xs font-semibold text-white break-words">
      {value}
    </span>
  </div>
);

// ---------------------------------------------------------------------------
// Seite
// ---------------------------------------------------------------------------

const ProTournamentInfoPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const [tournament, setTournament] = useState<ProTournamentInfo | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [bannerError, setBannerError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!slug) return;
      setLoading(true);
      setError(null);
      try {
        const data = await proTournamentInfoService.getBySlug(slug, { expand: true });
        if (cancelled) return;
        if (!data) {
          setError('Turnier nicht gefunden');
          setTournament(null);
        } else {
          setTournament(data);
        }
      } catch (e) {
        if (!cancelled) {
          setError('Fehler beim Laden des Turniers');
          setTournament(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [slug]);

  const keyFacts = useMemo<ProKeyFact[]>(
    () => proTournamentInfoService.getKeyFactsSorted(tournament),
    [tournament]
  );
  const formatRules = useMemo<ProFormatRule[]>(
    () => proTournamentInfoService.getFormatRulesSorted(tournament),
    [tournament]
  );
  const seededTeams = useMemo<ProTeam[]>(
    () => proTournamentInfoService.getSeededTeams(tournament),
    [tournament]
  );
  const otherTeams = useMemo<ProTeam[]>(
    () => proTournamentInfoService.getNonSeededTeams(tournament),
    [tournament]
  );
  const pastWinners = useMemo<ProPastWinner[]>(
    () => proTournamentInfoService.getPastWinnersSorted(tournament),
    [tournament]
  );
  const schedule = useMemo<ProScheduleEntry[]>(
    () => proTournamentInfoService.getScheduleSorted(tournament),
    [tournament]
  );

  if (loading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center bg-zinc-950">
        <Spinner size="lg" color="primary" />
      </div>
    );
  }

  if (error || !tournament) {
    return (
      <div className="flex min-h-[60vh] flex-col items-center justify-center bg-zinc-950 px-4 text-center">
        <Trophy size={48} className="mb-4 text-gray-600" />
        <h1 className="mb-2 text-xl font-bold text-white sm:text-2xl">
          {error || 'Turnier nicht gefunden'}
        </h1>
        <p className="mb-6 text-sm text-gray-400 sm:text-base">
          Das gesuchte Turnier konnte nicht geladen werden.
        </p>
        <Link to="/turniere">
          <Button className="bg-[#3fced1] font-bold text-black">
            Zurück zu den Turnieren
          </Button>
        </Link>
      </div>
    );
  }

  const t = tournament;
  const totalPrize = proTournamentInfoService.formatPrize(t);
  const dateRange = formatDateRange(t);
  const displayName = proTournamentInfoService.displayName(t);
  const bannerUrl = buildFileUrl(t.thumbnail || t.heroImage);

  const hasOverview = !!(t.overview || t.intro);
  const hasFormat = proTournamentInfoService.hasSection(t, 'format');
  const hasRecords = proTournamentInfoService.hasSection(t, 'records');
  const hasTeams = proTournamentInfoService.hasSection(t, 'teams');
  const hasGroups = proTournamentInfoService.hasSection(t, 'groups');
  const hasDraw = proTournamentInfoService.hasSection(t, 'draw');
  const hasSchedule = proTournamentInfoService.hasSection(t, 'schedule');
  const hasPrize = proTournamentInfoService.hasSection(t, 'prize');
  const hasPastWinners = proTournamentInfoService.hasSection(t, 'pastWinners');
  const hasNineDarters = proTournamentInfoService.hasSection(t, 'nineDarters');
  const hasQualification = proTournamentInfoService.hasSection(t, 'qualification');
  const hasParallel = proTournamentInfoService.hasSection(t, 'parallel');
  const hasSponsorHistory = proTournamentInfoService.hasSection(t, 'sponsorHistory');
  const hasBroadcasters = proTournamentInfoService.hasSection(t, 'broadcasters');

  // Sektionsnavigation: nur vorhandene Bereiche
  const navSections: { id: string; label: string }[] = [
    hasOverview && { id: 'ueberblick', label: 'Überblick' },
    hasTeams && { id: 'teilnehmer', label: 'Teilnehmer' },
    (hasGroups || hasDraw) && { id: 'auslosung', label: 'Auslosung' },
    hasSchedule && { id: 'spielplan', label: 'Spielplan' },
    hasPrize && { id: 'preisgeld', label: 'Preisgeld' },
    hasPastWinners && { id: 'sieger', label: 'Sieger' },
    hasRecords && { id: 'rekorde', label: 'Rekorde' },
    hasNineDarters && { id: 'neun-darter', label: '9-Darter' },
    hasParallel && { id: 'parallel', label: 'Parallel' },
  ].filter(Boolean) as { id: string; label: string }[];

  const scrollToSection = (id: string) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div className="min-h-screen overflow-x-hidden bg-zinc-950">
      <Helmet>
        <title>{t.seoTitle || `${displayName} · DartCloud`}</title>
        <meta
          name="description"
          content={
            t.seoDescription ||
            t.intro ||
            `Alle Infos zu ${displayName}: Teilnehmer, Preisgeld, Spielplan, Sieger.`
          }
        />
        {bannerUrl && <meta property="og:image" content={bannerUrl} />}
      </Helmet>

      {/* ================= HERO ================= */}
      <section className="detail-hero border-b border-zinc-800">
        <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 sm:py-10">
          {/* Breadcrumb */}
          <div className="mb-5 flex items-center gap-1.5 overflow-hidden text-[10px] uppercase tracking-widest text-gray-500 sm:text-xs">
            <Link to="/turniere" className="shrink-0 hover:text-[#3fced1]">
              Turniere
            </Link>
            {t.organization && (
              <>
                <ChevronRight size={10} className="shrink-0" />
                <span className="shrink-0">{t.organization}</span>
              </>
            )}
            <ChevronRight size={10} className="shrink-0" />
            <span className="truncate text-[#3fced1]">{t.shortName || t.name}</span>
          </div>

          <div className="grid gap-8 lg:grid-cols-[minmax(0,1fr)_400px] lg:items-center">
            {/* Links: Titel + Meta */}
            <div className="min-w-0">
              {/* Chips */}
              <div className="mb-4 flex flex-wrap items-center gap-1.5 sm:gap-2">
                {t.organization && (
                  <span className="rounded-full bg-[#3fced1] px-2.5 py-1 text-[11px] font-bold text-black">
                    {t.organization}
                  </span>
                )}
                {t.tier && (
                  <span className="rounded-full border border-amber-500/40 px-2.5 py-1 text-[11px] font-semibold text-amber-300">
                    {t.tier}
                  </span>
                )}
                {t.tournamentType && (
                  <span className="rounded-full border border-zinc-700 px-2.5 py-1 text-[11px] font-semibold text-gray-300">
                    {t.tournamentType}
                  </span>
                )}
                {t.status === 'LIVE' && (
                  <span className="inline-flex items-center gap-1.5 rounded-full bg-red-500 px-2.5 py-1 text-[11px] font-bold text-white">
                    <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-white" />
                    LIVE
                  </span>
                )}
                {t.status === 'COMPLETED' && (
                  <span className="rounded-full border border-zinc-600 bg-zinc-800/80 px-2.5 py-1 text-[11px] font-bold uppercase tracking-wide text-gray-300">
                    Beendet
                  </span>
                )}
              </div>

              <h1 className="break-words text-3xl font-black leading-[1.05] tracking-tight text-white sm:text-4xl lg:text-5xl">
                {t.name}
                {t.year && <span className="text-gray-500"> {t.year}</span>}
              </h1>

              {t.intro && (
                <p className="mt-3 max-w-2xl text-sm leading-relaxed text-gray-400 sm:text-base line-clamp-3">
                  {t.intro}
                </p>
              )}

              {/* Eckdaten-Strip */}
              <div className="mt-6 grid grid-cols-2 gap-2 sm:grid-cols-4 sm:gap-3">
                {dateRange && (
                  <div className="min-w-0 rounded-xl border border-[#3fced1]/30 bg-[#3fced1]/[0.06] p-3">
                    <div className="mb-1 flex items-center gap-1.5 text-[#3fced1]">
                      <Calendar size={13} className="shrink-0" />
                      <span className="truncate text-[9px] font-bold uppercase tracking-widest">
                        Termin
                      </span>
                    </div>
                    <p className="break-words text-xs font-bold leading-tight text-white sm:text-sm">
                      {dateRange}
                    </p>
                  </div>
                )}
                {(t.venue || t.city) && (
                  <div className="min-w-0 rounded-xl border border-zinc-800 bg-zinc-900/40 p-3">
                    <div className="mb-1 flex items-center gap-1.5 text-gray-400">
                      <MapPin size={13} className="shrink-0" />
                      <span className="truncate text-[9px] font-bold uppercase tracking-widest">
                        Ort
                      </span>
                    </div>
                    <p className="line-clamp-2 break-words text-xs font-bold leading-tight text-white sm:text-sm">
                      {t.venue || t.city}
                    </p>
                    {t.venue && t.city && (
                      <p className="truncate text-[10px] text-gray-400">{t.city}</p>
                    )}
                  </div>
                )}
                {totalPrize && (
                  <div className="min-w-0 rounded-xl border border-amber-500/30 bg-amber-500/[0.06] p-3">
                    <div className="mb-1 flex items-center gap-1.5 text-amber-300">
                      <Coins size={13} className="shrink-0" />
                      <span className="truncate text-[9px] font-bold uppercase tracking-widest">
                        Preisgeld
                      </span>
                    </div>
                    <p className="break-words text-xs font-bold leading-tight text-white sm:text-sm">
                      {totalPrize}
                    </p>
                  </div>
                )}
                {t.titleHolderTeamName && (
                  <div className="min-w-0 rounded-xl border border-zinc-800 bg-zinc-900/40 p-3">
                    <div className="mb-1 flex items-center gap-1.5 text-gray-400">
                      <Crown size={13} className="shrink-0 text-amber-400" />
                      <span className="truncate text-[9px] font-bold uppercase tracking-widest">
                        Titelverteidiger
                      </span>
                    </div>
                    <p className="line-clamp-2 break-words text-xs font-bold leading-tight text-white sm:text-sm">
                      {t.titleHolderTeamName}
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Rechts: KI-Banner (nur Desktop) */}
            {bannerUrl && !bannerError && (
              <div className="hidden overflow-hidden rounded-2xl border border-zinc-800 shadow-2xl shadow-black/40 lg:block">
                <img
                  src={bannerUrl}
                  alt={displayName}
                  onError={() => setBannerError(true)}
                  className="aspect-video w-full object-cover"
                />
              </div>
            )}
          </div>
        </div>
      </section>

      {/* ================= SEKTIONS-NAVIGATION (sticky) ================= */}
      {navSections.length > 1 && (
        <nav className="sticky top-16 z-30 border-b border-zinc-800 bg-zinc-950/90 backdrop-blur-md">
          <div className="mx-auto max-w-7xl px-4 sm:px-6">
            <div className="-mx-4 flex gap-2 overflow-x-auto px-4 py-2.5 sm:mx-0 sm:px-0 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
              {navSections.map((s) => (
                <button
                  key={s.id}
                  type="button"
                  onClick={() => scrollToSection(s.id)}
                  className="shrink-0 rounded-full border border-zinc-700 px-3.5 py-1.5 text-xs font-semibold text-gray-300 transition-colors hover:border-[#3fced1]/60 hover:text-white sm:text-sm"
                >
                  {s.label}
                </button>
              ))}
            </div>
          </div>
        </nav>
      )}

      {/* ================= INHALT: 70 / 30 ================= */}
      <div className="mx-auto max-w-7xl px-4 py-6 sm:px-6 sm:py-8">
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-10">
          {/* ---------- LINKE SPALTE (70%) ---------- */}
          <div className="min-w-0 space-y-6 lg:col-span-7">
            {/* Mobile: Banner oben */}
            {bannerUrl && !bannerError && (
              <div className="overflow-hidden rounded-2xl border border-zinc-800 lg:hidden">
                <img
                  src={bannerUrl}
                  alt={displayName}
                  onError={() => setBannerError(true)}
                  className="aspect-video w-full object-cover"
                />
              </div>
            )}

            {/* Überblick */}
            {hasOverview && (
              <ContentSection
                id="ueberblick"
                icon={<BookOpen size={18} />}
                eyebrow="Das Turnier"
                title="Über das Event"
              >
                <CollapsibleText text={t.overview || t.intro || ''} />
              </ContentSection>
            )}

            {/* Teilnehmer */}
            {hasTeams && (
              <ContentSection
                id="teilnehmer"
                icon={<Users size={18} />}
                eyebrow={`${(t.teams || []).length} im Feld`}
                title="Teilnehmer"
              >
                {seededTeams.length > 0 && (
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                    {seededTeams.map((team, idx) => {
                      const isTitleHolder =
                        !!t.titleHolderTeamName && team.name === t.titleHolderTeamName;
                      return (
                        <div
                          key={team.id || team.name || idx}
                          className={`min-w-0 rounded-xl border p-3.5 ${
                            isTitleHolder
                              ? 'border-[#3fced1]/40 bg-[#3fced1]/10'
                              : 'border-zinc-800 bg-zinc-950/60'
                          }`}
                        >
                          <div className="flex items-center justify-between gap-2">
                            <div className="flex min-w-0 items-center gap-2.5">
                              {team.flag && (
                                <span className="shrink-0 text-2xl leading-none">{team.flag}</span>
                              )}
                              <div className="min-w-0">
                                <p className="text-[10px] uppercase tracking-widest text-[#3fced1]">
                                  Seed #{team.seed ?? idx + 1}
                                </p>
                                <p className="truncate text-sm font-bold text-white sm:text-base">
                                  {team.name}
                                </p>
                              </div>
                            </div>
                            {isTitleHolder && (
                              <Crown size={16} className="shrink-0 text-amber-400" />
                            )}
                          </div>
                          {(team.players || []).some((p) => p.name && p.name !== team.name) && (
                            <p className="mt-1.5 truncate text-xs text-gray-400">
                              {(team.players || []).map((p) => p.name).join(' · ')}
                            </p>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}

                {otherTeams.length > 0 && (
                  <div
                    className={`grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3 ${
                      seededTeams.length > 0 ? 'mt-4' : ''
                    }`}
                  >
                    {otherTeams.map((team, idx) => (
                      <div
                        key={team.id || team.name || idx}
                        className="flex min-w-0 items-center gap-2.5 rounded-lg border border-zinc-800 bg-zinc-950/60 px-3 py-2.5"
                      >
                        {team.flag && <span className="shrink-0 text-xl">{team.flag}</span>}
                        <div className="min-w-0">
                          <p className="truncate text-sm font-semibold text-white">{team.name}</p>
                          {(team.players || []).some((p) => p.name && p.name !== team.name) && (
                            <p className="truncate text-[11px] text-gray-400">
                              {(team.players || []).map((p) => p.name).join(' · ')}
                            </p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </ContentSection>
            )}

            {/* Auslosung */}
            {(hasGroups || hasDraw) && (
              <ContentSection
                id="auslosung"
                icon={<Swords size={18} />}
                eyebrow="Wer gegen wen"
                title="Auslosung"
              >
                {hasGroups && (
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
                    {(t.groups || []).map((g, idx) => {
                      const teamsInGroup = (t.teams || []).filter((tm) =>
                        (g.teamIds || []).includes(tm.id || '')
                      );
                      return (
                        <div
                          key={g.name || idx}
                          className="rounded-xl border border-zinc-800 bg-zinc-950/60 p-3"
                        >
                          <p className="mb-2 text-sm font-bold tracking-wider text-[#3fced1]">
                            {g.name}
                          </p>
                          <div className="space-y-1.5">
                            {teamsInGroup.map((team, ti) => (
                              <div key={team.id || ti} className="flex min-w-0 items-center gap-2">
                                <span className="w-4 shrink-0 text-xs text-gray-500">{ti + 1}.</span>
                                {team.flag && <span className="shrink-0">{team.flag}</span>}
                                <span className="truncate text-sm font-medium text-white">
                                  {team.name}
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}

                {hasDraw && (
                  <div
                    className={`grid grid-cols-1 gap-2 sm:grid-cols-2 ${hasGroups ? 'mt-4' : ''}`}
                  >
                    {(t.drawPairings || [])
                      .slice()
                      .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
                      .map((pair: ProDrawPairing, idx) => (
                        <div
                          key={idx}
                          className="min-w-0 rounded-xl border border-zinc-800 bg-zinc-950/60 px-3.5 py-2.5"
                        >
                          {pair.round && (
                            <p className="mb-1 text-[10px] font-semibold uppercase tracking-widest text-gray-500">
                              {pair.round}
                            </p>
                          )}
                          <p className="truncate text-sm font-semibold text-white">
                            {pair.homeTeamName || '—'}
                          </p>
                          <p className="truncate text-sm text-gray-400">
                            {pair.awayTeamName || '—'}
                          </p>
                        </div>
                      ))}
                  </div>
                )}
              </ContentSection>
            )}

            {/* Spielplan */}
            {hasSchedule && (
              <ContentSection
                id="spielplan"
                icon={<Calendar size={18} />}
                eyebrow={dateRange || 'Sessions'}
                title="Spielplan"
              >
                <div className="space-y-2">
                  {schedule.map((s, idx) => {
                    const isFinal = idx === schedule.length - 1;
                    return (
                      <div
                        key={idx}
                        className={`flex min-w-0 items-center gap-3 rounded-xl border px-3.5 py-3 ${
                          isFinal
                            ? 'border-[#3fced1]/40 bg-[#3fced1]/10'
                            : 'border-zinc-800 bg-zinc-950/60'
                        }`}
                      >
                        <div
                          className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-sm font-bold ${
                            isFinal ? 'bg-[#3fced1] text-black' : 'bg-zinc-800 text-[#3fced1]'
                          }`}
                        >
                          {idx + 1}
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="truncate text-sm font-bold text-white">
                            {s.dateLabel || formatDate(s.date)}
                            {s.sessionLabel ? ` · ${s.sessionLabel}` : ''}
                          </p>
                          <p className="truncate text-xs text-gray-400">
                            {s.round || s.description || ''}
                          </p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </ContentSection>
            )}

            {/* Preisgeld */}
            {hasPrize && (
              <ContentSection
                id="preisgeld"
                icon={<Coins size={18} />}
                eyebrow={totalPrize ? `Gesamt: ${totalPrize}` : 'Aufschlüsselung'}
                title="Preisgeld"
              >
                <div className="space-y-2">
                  {(t.prizeBreakdown || []).map((p: ProPrizeBreakdown, idx) => (
                    <div
                      key={`${p.position}-${idx}`}
                      className={`flex min-w-0 items-center justify-between gap-3 rounded-xl border px-3.5 py-2.5 ${
                        idx === 0
                          ? 'border-amber-500/40 bg-amber-500/10'
                          : 'border-zinc-800 bg-zinc-950/60'
                      }`}
                    >
                      <div className="flex min-w-0 items-center gap-2">
                        {idx === 0 && <Trophy size={15} className="shrink-0 text-amber-400" />}
                        <div className="min-w-0">
                          <p className="truncate text-sm font-semibold text-white">{p.position}</p>
                          {p.count ? (
                            <p className="text-[10px] text-gray-500">{p.count}× vergeben</p>
                          ) : null}
                        </div>
                      </div>
                      <p
                        className={`shrink-0 text-sm font-extrabold sm:text-base ${
                          idx === 0 ? 'text-amber-300' : 'text-white'
                        }`}
                      >
                        {proTournamentInfoService.formatAmount(p.amount, p.currency)}
                        {/* "/ Team" nur bei echten Team-/Pairs-Turnieren zeigen –
                            bei Einzelturnieren (z.B. World Matchplay) ist es falsch.
                            perTeam allein reicht nicht (Schema-Default = true). */}
                        {p.perTeam &&
                          (t.tournamentType === 'TEAM' || t.tournamentType === 'PAIRS') && (
                            <span className="ml-1 text-[10px] font-medium text-gray-400">/ Team</span>
                          )}
                      </p>
                    </div>
                  ))}
                </div>

                {hasSponsorHistory && (
                  <>
                    <Divider className="my-4 bg-zinc-800" />
                    <p className="mb-2 text-xs font-semibold uppercase tracking-widest text-gray-500">
                      Sponsoren & Preisgeld-Historie
                    </p>
                    <div className="grid grid-cols-1 gap-1.5 sm:grid-cols-2">
                      {(t.sponsorHistory || [])
                        .slice()
                        .sort((a, b) => (b.year ?? 0) - (a.year ?? 0))
                        .map((s: ProSponsorHistoryEntry, idx) => (
                          <div
                            key={`${s.year}-${idx}`}
                            className="flex min-w-0 items-center justify-between gap-2 rounded-lg bg-zinc-950/60 px-3 py-2 text-xs"
                          >
                            <span className="shrink-0 font-bold text-white">{s.year}</span>
                            <span className="min-w-0 flex-1 truncate text-gray-400">
                              {s.sponsor || '—'}
                            </span>
                            <span className="shrink-0 font-semibold text-amber-300">
                              {proTournamentInfoService.formatAmount(
                                s.prizeMoney,
                                t.prizeMoneyCurrency
                              ) || '—'}
                            </span>
                          </div>
                        ))}
                    </div>
                  </>
                )}
              </ContentSection>
            )}

            {/* Sieger-Historie */}
            {hasPastWinners && (
              <ContentSection
                id="sieger"
                icon={<History size={18} />}
                eyebrow={t.firstHeldYear ? `Seit ${t.firstHeldYear}` : 'Hall of Fame'}
                title="Sieger"
              >
                {/* Mobile: kompakte Liste */}
                <div className="space-y-1.5 sm:hidden">
                  {pastWinners.map((w, idx) => (
                    <div
                      key={`${w.year}-${idx}`}
                      className={`flex min-w-0 items-center gap-3 rounded-lg border px-3 py-2 ${
                        idx === 0
                          ? 'border-amber-500/30 bg-amber-500/5'
                          : 'border-zinc-800 bg-zinc-950/60'
                      }`}
                    >
                      <span className="w-10 shrink-0 text-sm font-bold text-white">{w.year}</span>
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-semibold text-white">
                          {w.winnerTeamName}
                        </p>
                        {w.runnerUpTeamName && (
                          <p className="truncate text-[11px] text-gray-500">
                            vs. {w.runnerUpTeamName}
                          </p>
                        )}
                      </div>
                      {w.score && (
                        <span className="shrink-0 text-xs font-bold text-[#3fced1]">{w.score}</span>
                      )}
                    </div>
                  ))}
                </div>

                {/* Desktop: Tabelle */}
                <div className="hidden overflow-hidden rounded-xl border border-zinc-800 sm:block">
                  <table className="w-full text-sm">
                    <thead className="bg-zinc-950/80">
                      <tr className="text-left">
                        <th className="px-4 py-2.5 text-[11px] font-semibold uppercase tracking-widest text-gray-400">
                          Jahr
                        </th>
                        <th className="px-4 py-2.5 text-[11px] font-semibold uppercase tracking-widest text-gray-400">
                          Sieger
                        </th>
                        <th className="px-4 py-2.5 text-[11px] font-semibold uppercase tracking-widest text-gray-400">
                          Finalist
                        </th>
                        <th className="px-4 py-2.5 text-right text-[11px] font-semibold uppercase tracking-widest text-gray-400">
                          Ergebnis
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {pastWinners.map((w, idx) => (
                        <tr
                          key={`${w.year}-${idx}`}
                          className={`border-t border-zinc-800 transition-colors hover:bg-zinc-900/60 ${
                            idx === 0 ? 'bg-amber-500/5' : ''
                          }`}
                        >
                          <td className="px-4 py-2.5 font-bold text-white">{w.year}</td>
                          <td className="px-4 py-2.5 font-semibold text-white">
                            {idx === 0 && (
                              <Crown size={13} className="mr-1.5 -mt-0.5 inline text-amber-400" />
                            )}
                            {w.winnerTeamName}
                          </td>
                          <td className="px-4 py-2.5 text-gray-400">{w.runnerUpTeamName || '—'}</td>
                          <td className="px-4 py-2.5 text-right font-bold text-[#3fced1]">
                            {w.score || '—'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </ContentSection>
            )}

            {/* Rekorde */}
            {hasRecords && (
              <ContentSection
                id="rekorde"
                icon={<Award size={18} />}
                eyebrow="Zahlen & Fakten"
                title="Rekorde"
              >
                <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 xl:grid-cols-3">
                  {(t.records || []).map((r: ProRecord, idx) => (
                    <div
                      key={`${r.label}-${idx}`}
                      className="min-w-0 rounded-xl border border-zinc-800 bg-zinc-950/60 p-3"
                    >
                      <p className="truncate text-[10px] uppercase tracking-widest text-gray-500">
                        {r.label}
                      </p>
                      <p className="break-words text-base font-bold text-white">{r.value}</p>
                      {(r.holder || r.year || r.note) && (
                        <p className="mt-0.5 break-words text-xs text-gray-400">
                          {[r.holder, r.year, r.note].filter(Boolean).join(' · ')}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              </ContentSection>
            )}

            {/* 9-Darter */}
            {hasNineDarters && (
              <ContentSection
                id="neun-darter"
                icon={<Target size={18} />}
                eyebrow="Perfekte Legs"
                title="9-Darter-Geschichte"
              >
                <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
                  {(t.notableNineDarters || [])
                    .slice()
                    .sort((a, b) => (b.year ?? 0) - (a.year ?? 0))
                    .map((n: ProNotableNineDarter, idx) => (
                      <div
                        key={idx}
                        className="flex min-w-0 items-center gap-3 rounded-xl border border-zinc-800 bg-zinc-950/60 px-3.5 py-3"
                      >
                        <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-amber-500/30 bg-amber-500/10 text-sm font-extrabold text-amber-400">
                          {n.year}
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="truncate text-sm font-bold text-white">{n.playerName}</p>
                          <p className="truncate text-xs text-gray-400">
                            {[n.opponent ? `vs. ${n.opponent}` : null, n.round]
                              .filter(Boolean)
                              .join(' · ')}
                          </p>
                          {n.note && (
                            <p className="mt-0.5 line-clamp-2 text-[11px] text-gray-500">{n.note}</p>
                          )}
                        </div>
                      </div>
                    ))}
                </div>
              </ContentSection>
            )}

            {/* Parallel-Wettbewerbe */}
            {hasParallel && (
              <ContentSection
                id="parallel"
                icon={<Flag size={18} />}
                eyebrow="Begleitende Turniere"
                title="Parallel-Wettbewerbe"
              >
                <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                  {(t.parallelCompetitions || []).map((c: ProParallelCompetition, idx) => (
                    <div
                      key={`${c.name}-${idx}`}
                      className="min-w-0 rounded-xl border border-zinc-800 bg-zinc-950/60 p-4"
                    >
                      <p className="break-words text-sm font-bold text-white sm:text-base">
                        {c.name}
                      </p>
                      {c.description && (
                        <p className="mt-1.5 break-words text-xs text-gray-400 sm:text-sm">
                          {c.description}
                        </p>
                      )}
                      <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-400">
                        {c.prizeMoneyLabel && (
                          <span>
                            <Coins size={12} className="mr-1 -mt-0.5 inline text-amber-400" />
                            <strong className="text-white">{c.prizeMoneyLabel}</strong>
                          </span>
                        )}
                        {c.winnerName && (
                          <span>
                            <Crown size={12} className="mr-1 -mt-0.5 inline text-amber-400" />
                            <strong className="text-white">{c.winnerName}</strong>
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </ContentSection>
            )}
          </div>

          {/* ---------- RECHTE SPALTE (30%, sticky) ---------- */}
          <aside className="min-w-0 space-y-4 self-start lg:sticky lg:top-32 lg:col-span-3">
            {/* Eckdaten */}
            <SideCard icon={<ListChecks size={16} />} title="Eckdaten">
              <div className="divide-y divide-zinc-800">
                {dateRange && <InfoRow label="Termin" value={dateRange} />}
                {t.venue && <InfoRow label="Venue" value={t.venue} />}
                {(t.city || t.country) && (
                  <InfoRow
                    label="Ort"
                    value={[t.city, t.country].filter(Boolean).join(', ')}
                  />
                )}
                {totalPrize && <InfoRow label="Preisgeld" value={totalPrize} />}
                {t.trophyName && <InfoRow label="Trophäe" value={t.trophyName} />}
                {t.titleHolderTeamName && (
                  <InfoRow label="Titelverteidiger" value={t.titleHolderTeamName} />
                )}
                {typeof t.totalTeams === 'number' && (
                  <InfoRow label="Teilnehmer" value={String(t.totalTeams)} />
                )}
                {t.firstHeldYear && <InfoRow label="Erstaustragung" value={String(t.firstHeldYear)} />}
                {t.sponsor && <InfoRow label="Sponsor" value={t.sponsor} />}
              </div>
            </SideCard>

            {/* Spieldistanzen / Modus */}
            {hasFormat && (
              <SideCard icon={<Swords size={16} />} title="Modus">
                <div className="space-y-1.5">
                  {formatRules.map((rule, idx) => (
                    <div
                      key={`${rule.label}-${idx}`}
                      className="min-w-0 rounded-lg bg-zinc-950/60 px-3 py-2"
                    >
                      <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-500">
                        {rule.label}
                      </p>
                      <p className="break-words text-xs font-semibold text-white">{rule.value}</p>
                    </div>
                  ))}
                </div>
                {t.formatSummary && (
                  <p className="mt-3 break-words text-[11px] leading-relaxed text-gray-500">
                    {t.formatSummary}
                  </p>
                )}
              </SideCard>
            )}

            {/* Key Facts */}
            {keyFacts.length > 0 && (
              <SideCard icon={<Star size={16} />} title="Key Facts">
                <div className="divide-y divide-zinc-800">
                  {keyFacts.map((fact, idx) => (
                    <InfoRow key={`${fact.label}-${idx}`} label={fact.label || ''} value={fact.value || ''} />
                  ))}
                </div>
              </SideCard>
            )}

            {/* Qualifikation */}
            {hasQualification && (
              <SideCard icon={<Target size={16} />} title="Qualifikation">
                <div className="space-y-1.5">
                  {(t.qualificationCriteria || []).map((q: ProQualificationCriterion, idx) => (
                    <div
                      key={`${q.label}-${idx}`}
                      className="min-w-0 rounded-lg bg-zinc-950/60 px-3 py-2"
                    >
                      <div className="flex items-center justify-between gap-2">
                        <p className="min-w-0 break-words text-xs font-semibold text-white">
                          {q.label}
                        </p>
                        {typeof q.count === 'number' && (
                          <span className="shrink-0 rounded-full bg-[#3fced1] px-2 py-0.5 text-[10px] font-bold text-black">
                            {q.count}
                          </span>
                        )}
                      </div>
                      <p className="mt-0.5 break-words text-[11px] text-gray-400">{q.value}</p>
                    </div>
                  ))}
                </div>
              </SideCard>
            )}

            {/* TV & Streams */}
            {hasBroadcasters && (
              <SideCard icon={<Tv size={16} />} title="TV & Streams">
                <div className="space-y-1.5">
                  {(t.broadcasters || []).map((b, idx) => (
                    <div
                      key={`${b.name}-${idx}`}
                      className="flex min-w-0 items-center justify-between gap-2 rounded-lg bg-zinc-950/60 px-3 py-2"
                    >
                      <span className="min-w-0 truncate text-xs font-semibold text-white">
                        {b.name}
                        {b.countryCode && (
                          <span className="ml-1.5 text-[10px] text-gray-500">{b.countryCode}</span>
                        )}
                      </span>
                      {b.url && (
                        <a
                          href={b.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          aria-label={`${b.name} öffnen`}
                          className="shrink-0 text-gray-500 hover:text-[#3fced1]"
                        >
                          <ExternalLink size={13} />
                        </a>
                      )}
                    </div>
                  ))}
                </div>
              </SideCard>
            )}

            {/* Offizielle Website */}
            {t.officialWebsite && (
              <a
                href={t.officialWebsite}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-2 rounded-xl border border-zinc-700 px-4 py-3 text-sm font-semibold text-white transition-colors hover:border-[#3fced1]/60 hover:bg-zinc-900"
              >
                <Globe2 size={16} style={{ color: ACCENT }} />
                Offizielle Website
                <ExternalLink size={13} className="text-gray-500" />
              </a>
            )}
          </aside>
        </div>
      </div>
    </div>
  );
};

export default ProTournamentInfoPage;
